import type {
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query/react";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import getHeader from "app/services/api/getHeader";
import { setLogoutRequested } from "app/store/slices/authSlice";
import { UNAUTHORIZED_CODE } from "app/shared/constants/constants";
import { encryptURI } from "app/shared/utils/ValueEncryption";
import { EncryptedResponse } from "app/services/api/commonTypes";
import {
    StoreMenu, ItemDetailsResponse, MenuResponse, ItemSummary,
    CreateUpdateOrderResponse,
    CreateUpdateOrderRequest,
    OrderPlacementResponse,
    OrderPlacementRequest,
    LoyaltyRewardsResponse,
    LoyaltyRewardsRequest,
    UpsellData,
    UpsellResponse,
} from "store/slices/tOrderTypes";
import { decrypt, encrypt } from "app/shared/utils/EncryptionUtility";
import { RootState } from "app/store";
import FeatureFlags from "app/shared/constants/FeatureFlags";

type IGetMenuItemDetailsArgs = {
    selectedItemMultiLocationId: string;
    restaurantGuid: string;
    restaurantGroupId: number;
};
const baseUrl = process.env.REACT_APP_ALLEGIANCE_API_URL;
const TOAST_UPSELLS_API_PATH = "/api/store/restaurant-groups";
const rawBaseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => getHeader(
        getState,
        headers,
        { isToastRequest: true },
    ),
    credentials: window.location.hostname === "localhost" ? "include" : undefined,
});

export const transformUpsellsResponse = (response: EncryptedResponse): UpsellData => {
    const decryptedUpsellsResponse = JSON.parse(decrypt(response.value)) as UpsellResponse;
    return decryptedUpsellsResponse.data;
};

export const customBaseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    object,
    FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
    if ((api.getState() as RootState).auth.configs.env[FeatureFlags.TOAST] !== "true") {
        return {
            error: {
                status: "CUSTOM_ERROR",
                error: "Toast integration is disabled",
            },
        };
    }

    const result = await rawBaseQuery(args, api, extraOptions);
    if (result.error && result.error.status === UNAUTHORIZED_CODE) {
        // Run logout
        api.dispatch(setLogoutRequested(true));
    }

    return result;
};

// Define a service using a base URL and expected endpoints
export const tOrderApi = createApi({
    reducerPath: "tMenu",
    baseQuery: customBaseQuery,
    endpoints: (builder) => ({
        getMenu: builder.query<StoreMenu, string>({
            query: (restaurantGuid) => {
                const encryptedRestaurantGuid = encryptURI(restaurantGuid);
                return {
                    url: `/api/store/menu/${encryptedRestaurantGuid}`,
                    method: "GET",
                };
            },
            transformResponse: (response: EncryptedResponse) => {
                const decryptedMenuResponse = JSON.parse(decrypt(response.value)) as MenuResponse;
                /*
                 * Stripping the errors property from the response and
                 * sending only the menu content to component
                 */
                const storeMenu = decryptedMenuResponse.data;
                return storeMenu;
            },
            keepUnusedDataFor: Number(process.env.REACT_APP_CACHE_REFRESH),
        }),
        getMenuItemDetails: builder.query<ItemSummary, IGetMenuItemDetailsArgs>({
            query: ({ selectedItemMultiLocationId, restaurantGuid, restaurantGroupId }) => ({
                url: [
                    "/api/store/restaurant-groups",
                    encryptURI(String(restaurantGroupId)),
                    "restaurant",
                    encryptURI(restaurantGuid),
                    "menu-item",
                    encryptURI(selectedItemMultiLocationId),
                ].join("/"),
                method: "GET",
            }),

            transformResponse: (response: EncryptedResponse) => {
                const decryptedItemDetailsResponse = JSON.parse(
                    decrypt(response.value),
                ) as ItemDetailsResponse;
                /*
                 * Stripping the errors property from the response and
                 * sending only the item content to component
                 */
                return decryptedItemDetailsResponse.data;
            },
            keepUnusedDataFor: Number(process.env.REACT_APP_CACHE_REFRESH),
        }),
        getUpsells: builder.query<UpsellData, number>({
            query: (restaurantGroupId) => ({
                url: `${TOAST_UPSELLS_API_PATH}/${encryptURI(String(restaurantGroupId))}/upsells`,
                method: "GET",
            }),
            transformResponse: transformUpsellsResponse,
            keepUnusedDataFor: Number(process.env.REACT_APP_CACHE_REFRESH),
        }),
        createOrder: builder.mutation<
            CreateUpdateOrderResponse["data"],
            CreateUpdateOrderRequest & { storeId: string }
        >({
            query: ({ storeId, ...orderRequestPayload }) => ({
                url: `/api/store/${encryptURI(String(storeId))}/order/create`,
                method: "POST",
                body: JSON.stringify({value: encrypt(JSON.stringify(orderRequestPayload))}),
            }),
            transformResponse: (response: EncryptedResponse) => {
                const decryptedCreateOrderResponse = JSON.parse(
                    decrypt(response.value),
                ) as CreateUpdateOrderResponse;
                return decryptedCreateOrderResponse.data;
            },
            // transformResponse: (response: CreateUpdateOrderResponse) => response.data,
        }),
        updateOrder: builder.mutation<
            CreateUpdateOrderResponse["data"],
            CreateUpdateOrderRequest & { storeId: string; orderId: string }
        >({
            query: ({ storeId, orderId, ...orderRequestPayload }) => ({
                url: `/api/store/${encryptURI(String(storeId))}/order/${encryptURI(String(orderId))}/update`,
                method: "PUT",
                body: JSON.stringify({value: encrypt(JSON.stringify(orderRequestPayload))}),
            }),

            transformResponse: (response: EncryptedResponse) => {
                const decryptedUpdateOrderResponse = JSON.parse(
                    decrypt(response.value),
                ) as CreateUpdateOrderResponse;
                return decryptedUpdateOrderResponse.data;
            },
        }),
        placeOrder: builder.mutation<
            OrderPlacementResponse["data"],
            OrderPlacementRequest
        >({
            query: ({
                storeId, orderId, customer: { phone, ...customer }, ...orderPlacementRequest
            }: OrderPlacementRequest) => ({
                url: `/api/store/${encryptURI(String(storeId))}/order/${encryptURI(String(orderId))}/place`,
                method: "POST",
                body: JSON.stringify({
                    value: encrypt(JSON.stringify({
                        ...orderPlacementRequest,
                        customer: {
                            ...customer,
                            ...(phone?.trim() ? { phone: phone.trim() } : {}),
                        },
                    })),
                }),
            }),
            transformResponse: (response: EncryptedResponse) => {
                const decryptedPlaceOrderResponse = JSON.parse(
                    decrypt(response.value),
                ) as OrderPlacementResponse;
                return decryptedPlaceOrderResponse.data;
            },
        }),
        loyaltyRewards: builder.query<LoyaltyRewardsResponse["data"], LoyaltyRewardsRequest>({
            query: (loyaltyRewardPayload:LoyaltyRewardsRequest) => ({
                url: "/api/loyalty/rewards",
                method: "POST",
                body: JSON.stringify({value: encrypt(JSON.stringify(loyaltyRewardPayload))}),
            }),
            transformResponse: (response: EncryptedResponse) => {
                const decryptedLoyaltyRewardsResponse = JSON.parse(
                    decrypt(response.value),
                ) as LoyaltyRewardsResponse;
                return decryptedLoyaltyRewardsResponse.data;
            },
            keepUnusedDataFor: 0,
        }),
    }),
});

export const {
    useGetMenuQuery,
    useGetMenuItemDetailsQuery,
    useGetUpsellsQuery,
    useCreateOrderMutation,
    useUpdateOrderMutation,
    usePlaceOrderMutation,
    useLoyaltyRewardsQuery,
} = tOrderApi;
